"use client";

import React, { useState, useMemo } from "react";
import { Search, Phone, Mail, Globe, Building2, MapPin } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
function BusinessGridConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const categories = useMemo(() => {
    if (!posts) return ["All"];
    const cats = new Set<string>();
    posts.forEach(post => {
      const cat = post.customFields?.business_categories || "General";
      cats.add(cat);
    });
    return ["All", ...Array.from(cats).sort()];
  }, [posts]);
  const filteredPosts = useMemo(() => {
    if (!posts) return [];
    return posts.filter(post => {
      const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase());
      const postCategory = post.customFields?.business_categories || "General";
      const matchesCategory = selectedCategory === "All" || postCategory === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [posts, searchTerm, selectedCategory]);
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-destructive mb-4">Failed to load business directory.</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>;
  }
  return <div className="space-y-8">
      {}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-4 rounded-xl border shadow-sm">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input type="text" placeholder="Search businesses by name..." className="pl-9 w-full" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        <div className="w-full sm:w-auto min-w-[200px]">
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger>
              <SelectValue placeholder="Select Category" />
            </SelectTrigger>
            <SelectContent>
              {categories.map((cat, i) => <SelectItem key={cat} value={cat} data-index={i}>
                  {cat}
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {}
      {loading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="animate-pulse h-[300px]">
              <CardHeader className="space-y-2">
                <div className="h-6 bg-muted rounded w-2/3"></div>
                <div className="h-4 bg-muted rounded w-1/3"></div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="h-20 bg-muted rounded w-full"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded w-1/2"></div>
                  <div className="h-4 bg-muted rounded w-3/4"></div>
                </div>
              </CardContent>
            </Card>)}
        </div> : filteredPosts.length === 0 ? <div className="text-center py-24 bg-muted/30 rounded-xl border border-dashed">
          <Building2 className="size-12 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No businesses found</h3>
          <p className="text-muted-foreground">
            Try adjusting your search or category filter to find what you're looking for.
          </p>
          {(searchTerm || selectedCategory !== "All") && <Button variant="link" onClick={() => {
        setSearchTerm("");
        setSelectedCategory("All");
      }} className="mt-4">
              Clear all filters
            </Button>}
        </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPosts.map((post, i) => {
        const category = post.customFields?.business_categories || "General";
        const description = post.customFields?.business_description;
        const phone = post.customFields?.contact_phone;
        const email = post.customFields?.contact_email;
        const website = post.customFields?.website_url;
        return <Card key={post.id} data-index={i} className="flex flex-col h-full overflow-hidden hover:shadow-md transition-shadow duration-200">
                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start gap-4 mb-2">
                    <CardTitle className="text-xl font-default leading-tight">
                      {post.title}
                    </CardTitle>
                    <Badge variant="secondary" className="shrink-0">
                      {category}
                    </Badge>
                  </div>
                </CardHeader>
                
                <CardContent className="flex-1 flex flex-col gap-4">
                  <p className="text-sm text-muted-foreground line-clamp-3 flex-1">
                    {description || "No description available for this business."}
                  </p>
                  
                  <div className="space-y-2.5 text-sm pt-4 border-t border-border/50">
                    {phone && <div className="flex items-center gap-3 text-foreground">
                        <Phone className="size-4 text-primary shrink-0" />
                        <span>{phone}</span>
                      </div>}
                    {email && <div className="flex items-center gap-3 text-foreground">
                        <Mail className="size-4 text-primary shrink-0" />
                        <span className="truncate">{email}</span>
                      </div>}
                    {website && <div className="flex items-center gap-3 text-foreground">
                        <Globe className="size-4 text-primary shrink-0" />
                        <span className="truncate">{website.replace(/^https?:\/\//, '')}</span>
                      </div>}
                  </div>
                </CardContent>
                
                <CardFooter className="pt-4 border-t bg-muted/10">
                  <Button asChild className="w-full" variant="default">
                    <Link to={post.link || "#"}>
                      View Business Profile
                    </Link>
                  </Button>
                </CardFooter>
              </Card>;
      })}
        </div>}
    </div>;
}
export default function BusinessGridWithFiltering() {
  const wp_query_params = {
    post_type: "business_directory",
    per_page: 100,
    orderby: "title",
    order: "asc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="business-grid-with-filtering" className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4">
            Business Directory
          </h2>
          <p className="text-lg text-muted-foreground">
            Discover and connect with our network of trusted local businesses and partners.
          </p>
        </div>

        <PostsProvider wp_query={{
        post_type: "business_directory",
        per_page: 100,
        orderby: "title",
        order: "asc"
      }}>
          <BusinessGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}